var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { ProjectStatus } from 'root/src/shared/@types/project';
import toast from 'react-hot-toast';
import { useCreateProjectDropMutation } from 'root/src/client/v2/drop/hooks/useCreateProjectDropMutation';
import { useInitialRewardsQuery } from 'root/src/client/v2/drop/hooks/useInitialRewardQuery';
import { useUserDropEventLandingShortUrlQuery } from 'root/src/client/v2/drop/hooks/useDropEventLandingShortUrlQuery';
import { z } from 'zod';
import { useFormContext, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createContext, useContext, useEffect } from 'react';
import { useLocalStorage } from 'root/src/client/web/hooks/useLocalStorage';
import { InitialProductDisplayModal } from 'root/src/client/v2/drop/features/KeySelectionModal/components/InitialProductDisplayModal';
import { PendingProjectModal } from 'root/src/client/v2/drop/features/KeySelectionModal/components/PendingProjectModal';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useQuery } from '@tanstack/react-query';
import { Platform } from 'root/src/shared/@types/platforms';
import { ScopeSet } from 'root/src/shared/@types/auth';
import { Provider } from 'root/src/shared/@types/providers';
import { SignInWithProviderButton } from 'root/src/client/v2/auth/components/SignInWithProviderButton';
import { ModalLayout } from 'root/src/client/v2/common/layouts/ModalLayout';
import { ButtonNew, ButtonNewVariant } from 'root/src/client/v2/common/components/form/ButtonNew';
const PRODUCT_POLLING_TIME = 3000;
// Default (legacy) chat-permission re-auth prompt. The redesign passes its own component.
const LegacyChatPermissionModal = ({ onGranted, onCancel }) => (<ModalLayout className="sm:min-w-[500px]">
		<div className="flex flex-col justify-center items-center gap-6">
			<h2 className="font-bold text-xl text-center">Permission needed</h2>
			<p className="text-center text-light-4 text-sm">
				To share announcements in your chat, we need additional permissions on your Twitch account.
				Please re-authenticate to grant access.
			</p>
			<div className="flex gap-4">
				<SignInWithProviderButton provider={Provider.Twitch} scopeSet={ScopeSet.creatorPlus} popup popupOptions={{ onSuccess: onGranted, onError: onCancel }}/>
				<ButtonNew type="button" variant={ButtonNewVariant.Ghost} onClick={onCancel} className="font-bold uppercase text-center">
					Cancel
				</ButtonNew>
			</div>
		</div>
	</ModalLayout>);
// `overrides` lets the redesign swap the post-submit modals (defaults to the legacy ones).
export function useKeySelectionModal(params, overrides = {}) {
    var _a, _b, _c, _d, _e, _f;
    const { dropEvent, dare, payload } = params;
    const PendingProjectComponent = (_a = overrides.PendingProjectModal) !== null && _a !== void 0 ? _a : PendingProjectModal;
    const InitialProductDisplayComponent = (_b = overrides.InitialProductDisplayModal) !== null && _b !== void 0 ? _b : InitialProductDisplayModal;
    const isSingleProduct = ((_c = dropEvent.initialProduct) !== null && _c !== void 0 ? _c : []).length === 1;
    const form = useKeySelectionForm({
        defaultValues: isSingleProduct ? getFormDefaultValues(dropEvent) : undefined,
    });
    const createProjectDropMutation = useCreateProjectDropMutation();
    const setChatLinkSharingMutation = trpc.project.setChatLinkSharing.useMutation();
    const userLandingShortUrlQuery = useUserDropEventLandingShortUrlQuery(dropEvent.id, {
        enabled: false,
    });
    const modal = useModal();
    const defaultShow = (node, options) => {
        var _a;
        return modal.show(node, { isCloseable: (_a = options === null || options === void 0 ? void 0 : options.closeable) !== null && _a !== void 0 ? _a : true });
    };
    const showModal = (_d = overrides.showModal) !== null && _d !== void 0 ? _d : defaultShow;
    const hideModal = (_e = overrides.hideModal) !== null && _e !== void 0 ? _e : modal.hide;
    const ChatPermissionComponent = (_f = overrides.chatPermissionModalComponent) !== null && _f !== void 0 ? _f : LegacyChatPermissionModal;
    const utils = trpc.useUtils();
    const initialRewardsQuery = useInitialRewardsQuery(dropEvent.id, {
        enabled: false,
    });
    const initialProductPollingQuery = useQuery({
        queryKey: ['initialProduct', 'polling'],
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const res = yield initialRewardsQuery.refetch();
            if (res.isError) {
                throw new Error(res.error.message);
            }
            const items = res.data;
            if (!(items === null || items === void 0 ? void 0 : items.length)) {
                throw new Error('No initial product found, please contact support');
            }
            return items;
        }),
        enabled: false,
        refetchInterval: PRODUCT_POLLING_TIME,
        retry: 30,
    });
    useEffect(() => {
        if (isSingleProduct) {
            form.reset(getFormDefaultValues(dropEvent));
        }
    }, [isSingleProduct]);
    const getProducts = () => __awaiter(this, void 0, void 0, function* () {
        var _g, _h;
        const response = yield initialProductPollingQuery.refetch();
        if (response.isError) {
            throw new Error(response.error.message);
        }
        return (_h = (_g = response.data) === null || _g === void 0 ? void 0 : _g.flatMap(item => item.rewards)) !== null && _h !== void 0 ? _h : [];
    });
    const getChosenInitialProductIndex = (values) => {
        var _a;
        if (values.userHasGame) {
            return undefined;
        }
        const index = (_a = dropEvent.initialProduct) === null || _a === void 0 ? void 0 : _a.findIndex(product => product.id === values.productId);
        if (index === undefined || index === -1) {
            throw new Error('Unsupported initial product', {
                cause: {
                    productId: values.productId,
                },
            });
        }
        return index;
    };
    const onFormSubmit = form.handleSubmit((values) => __awaiter(this, void 0, void 0, function* () {
        try {
            const chosenInitialProductIndex = getChosenInitialProductIndex(values);
            const project = yield createProjectDropMutation.mutateAsync(Object.assign(Object.assign({}, payload), { chosenInitialProductIndex, userHasGame: values.userHasGame }));
            if (project.status === ProjectStatus.Pending) {
                showModal(<PendingProjectComponent />);
                return;
            }
            if (values.userHasGame) {
                hideModal();
                return;
            }
            const products = yield getProducts();
            const shortUrlQuery = yield userLandingShortUrlQuery.refetch();
            if (shortUrlQuery.isError) {
                throw new Error(shortUrlQuery.error.message);
            }
            const onEnableChatSharing = () => __awaiter(this, void 0, void 0, function* () {
                const hasValidScope = yield utils.auth.hasValidTokenScope.fetch({
                    platform: Platform.Twitch,
                    scopeSet: ScopeSet.creatorPlus,
                });
                if (hasValidScope) {
                    yield setChatLinkSharingMutation.mutateAsync({
                        dropId: dropEvent.id,
                        enabled: true,
                        intervalMode: 'auto',
                    });
                    hideModal();
                }
                else {
                    showModal(<ChatPermissionComponent onGranted={() => __awaiter(this, void 0, void 0, function* () {
                            yield setChatLinkSharingMutation.mutateAsync({
                                dropId: dropEvent.id,
                                enabled: true,
                                intervalMode: 'auto',
                            });
                            hideModal();
                        })} onCancel={hideModal}/>);
                }
            });
            showModal(<InitialProductDisplayComponent 
            // TODO: Fix typing
            // @ts-ignore
            dropEvent={dropEvent} shortUrl={shortUrlQuery.data} products={products} dare={dare} linkMarketingCopy={dropEvent.linkMarketingCopy} onEnableChatSharing={onEnableChatSharing}/>, {
                closeable: false,
            });
        }
        catch (err) {
            hideModal();
            console.error(err);
            const message = isLegacyError(err)
                ? err.error : err instanceof Error ? err.message : 'Dare cannot be created';
            toast.error(message, {
                position: 'bottom-center',
                duration: 10000,
            });
        }
    }));
    return {
        form,
        onFormSubmit,
    };
}
export const keySelectionFormSchema = z.discriminatedUnion('userHasGame', [
    z.object({
        userHasGame: z.literal(true),
    }),
    z.object({
        userHasGame: z.literal(false),
        productId: z.string(),
    }),
]);
export function useKeySelectionForm(params) {
    return useForm(Object.assign(Object.assign({}, params), { resolver: zodResolver(keySelectionFormSchema) }));
}
export function useKeySelectionFormContext() {
    return useFormContext();
}
export const KeySelectionModalContext = createContext(null);
export function useKeySelectionModalContext() {
    const context = useContext(KeySelectionModalContext);
    if (!context) {
        throw new Error('useKeySelectionModalContext must be used within a KeySelectionModalProvider');
    }
    return context;
}
const DISMISS_ALREADY_OWN_PRODUCT_MODAL = 'DISMISS_ALREADY_OWN_PRODUCT_MODAL';
export function useDismissAlreadyOwnProductModal() {
    return useLocalStorage(DISMISS_ALREADY_OWN_PRODUCT_MODAL, false);
}
function isLegacyError(err) {
    return typeof err === 'object' && err !== null && 'error' in err;
}
function getFormDefaultValues(dropEvent) {
    var _a;
    const { initialProduct = [] } = dropEvent;
    return {
        userHasGame: false,
        productId: (_a = initialProduct.at(0)) === null || _a === void 0 ? void 0 : _a.id,
    };
}
